import type {FC} from 'react';
import {useState} from 'react';
import type {AssistantMemoryResponse} from 'src/api/ProactApiClient';
import {Alert} from 'src/design/atoms/Alert';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SideDrawer} from 'src/design/organisms/SideDrawer';
import {
  useAssistantUserMemoryQuery,
  useDeleteAssistantUserMemoryMutation,
} from 'src/redux/queries/assistantControlCenter';
import {AssistantMemoryNotesTable} from './AssistantMemoryNotesTable';
import {useMemoryRequest} from './useMemoryRequest';

export interface AssistantUserMemoryDrawerProps {
  /** The user whose notes are shown; null keeps the drawer closed. */
  user: {userId: number; userName: string} | null;
  onClose: () => void;
}

/**
 * Control Center drawer listing one user's Scout memory notes; admins can delete, not edit.
 * @registry Admin read-only view of a user's Scout memory notes with Delete.
 */
export const AssistantUserMemoryDrawer: FC<AssistantUserMemoryDrawerProps> = ({
  user,
  onClose,
}) => {
  const run = useMemoryRequest();
  const {data, isLoading, isError} = useAssistantUserMemoryQuery(
    user?.userId ?? 0,
    {skip: user === null}
  );
  const [deleteNote] = useDeleteAssistantUserMemoryMutation();
  const [pendingDelete, setPendingDelete] =
    useState<AssistantMemoryResponse | null>(null);

  const confirmDelete = async () => {
    const note = pendingDelete;
    setPendingDelete(null);
    if (!note || !user) return;
    await run(
      () => deleteNote({userId: user.userId, id: note.id}).unwrap(),
      'Memory note deleted.'
    );
  };

  return (
    <>
      <SideDrawer opened={user !== null} onClose={onClose} size="720px">
        <SideDrawer.Header
          title={user ? `Scout memory - ${user.userName}` : 'Scout memory'}
        />
        <SideDrawer.Body loading={isLoading}>
          {isError ? (
            <Alert color="red" title="Unable to load memory">
              This user's memory notes could not be loaded. Please try again.
            </Alert>
          ) : data ? (
            <AssistantMemoryNotesTable
              notes={data}
              isLoading={false}
              isError={false}
              emptyText="This user has no memory notes."
              onDelete={setPendingDelete}
            />
          ) : null}
        </SideDrawer.Body>
      </SideDrawer>
      <ConfirmDialog
        opened={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Delete memory note"
        body={`Delete the note "${pendingDelete?.text ?? ''}"? Scout will stop using it for this user.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete"
        buttonTextNo="Cancel"
        size="sm"
      />
    </>
  );
};
